// Paginación del documento del acta por alto medido.
//
// Antes cada hoja de resoluciones llevaba 3 puntos fijos: si el texto no
// entraba, la hoja quedaba más alta que una A4 y al pasarla al PDF se
// aplastaba (piloto Founders, 26-oct-2026). Ahora el documento es una
// secuencia de bloques (párrafos, filas de asistencia, cabezas de punto…)
// que el Template mide en el navegador, y acá se reparten en hojas.

/** Milímetros CSS a píxeles CSS (96 px por pulgada, como el navegador). */
export const mmAPx = (mm) => (mm * 96) / 25.4;

// Hoja A4 de contenido: 20 mm de margen arriba y el pie (que arranca a ~19 mm
// del borde de abajo) con aire. Coincide con .page-flujo en actaTemplate.css.
export const ALTO_UTIL_PX = Math.floor(mmAPx(297 - 20 - 27));
// Colchón para redondeos de medición entre bloques.
export const MARGEN_SEGURIDAD_PX = 8;

/** Largo a partir del cual un párrafo se parte en oraciones (un bloque nunca supera una hoja). */
export const MAX_CARACTERES_PARRAFO = 900;

/**
 * Reparte bloques medidos en hojas.
 *
 * bloques: [{ key, alto, juntoAlSiguiente?, cont? }]
 *   juntoAlSiguiente  no puede quedar último en la hoja (títulos, cabeza de un punto):
 *                     la cadena hasta el primer bloque que no lo tenga pasa entera de hoja
 *                     si no entra (salvo que la cadena sola sea más alta que una hoja).
 *   cont              grupo al que pertenece ("asist", "agenda", "p:<id>"): si el bloque
 *                     abre una hoja, va precedido por la cabecera de continuación del grupo.
 * capacidad: px útiles por hoja.
 * altoCont: { [grupo]: px } alto de cada cabecera de continuación.
 *
 * → hojas: [[{ key, cont?: true }]]  (las cabeceras de continuación con cont: true y la key del grupo)
 */
export function paginar(bloques, capacidad, altoCont = {}) {
  const hojas = [];
  let hoja = [];
  let usado = 0;
  const nueva = () => {
    hoja = [];
    hojas.push(hoja);
    usado = 0;
  };
  nueva();
  const colocar = (b) => {
    if (hoja.length === 0 && b.cont) {
      hoja.push({ key: b.cont, cont: true });
      usado += Number(altoCont[b.cont]) || 0;
    }
    hoja.push({ key: b.key });
    usado += Number(b.alto) || 0;
  };
  const lista = bloques || [];
  let i = 0;
  while (i < lista.length) {
    let j = i;
    while (j < lista.length - 1 && lista[j].juntoAlSiguiente) j += 1;
    const cadena = lista.slice(i, j + 1);
    const alto = cadena.reduce((s, b) => s + (Number(b.alto) || 0), 0);
    if (hoja.length > 0 && usado + alto > capacidad && alto <= capacidad) nueva();
    for (const b of cadena) {
      if (hoja.length > 0 && usado + (Number(b.alto) || 0) > capacidad) nueva();
      colocar(b);
    }
    i = j + 1;
  }
  return hojas.filter((h) => h.length > 0);
}

/**
 * Oraciones de un texto: corta después de . ! ? o … seguidos de un espacio
 * ("100.000" no se corta). Sin lookbehind en la regex: Safari anterior a 16.4
 * no lo entiende y el bundle entero no carga.
 */
function oracionesDe(t) {
  const out = [];
  let inicio = 0;
  for (let i = 0; i < t.length; i += 1) {
    if (".!?…".includes(t[i]) && /\s/.test(t[i + 1] || "")) {
      out.push(t.slice(inicio, i + 1).trim());
      inicio = i + 1;
    }
  }
  out.push(t.slice(inicio).trim());
  return out.filter(Boolean);
}

/**
 * Parte un párrafo muy largo en tramos de oraciones completas (y, si una sola
 * oración es más larga que el máximo, por palabras), para que ningún bloque
 * supere una hoja. Un párrafo normal vuelve tal cual.
 */
export function partirParrafo(texto, max = MAX_CARACTERES_PARRAFO) {
  const t = String(texto || "").trim();
  if (t.length <= max) return t ? [t] : [];
  const oraciones = oracionesDe(t);
  const tramos = [];
  let actual = "";
  const empujar = (s) => {
    if (!actual) actual = s;
    else if (actual.length + 1 + s.length <= max) actual = `${actual} ${s}`;
    else {
      tramos.push(actual);
      actual = s;
    }
  };
  for (const o of oraciones) {
    if (o.length <= max) {
      empujar(o);
      continue;
    }
    for (const palabra of o.split(/\s+/)) empujar(palabra);
  }
  if (actual) tramos.push(actual);
  return tramos;
}

// ───────────── Saltos de renglón y viñetas en los textos libres ─────────────
// En el formulario cada renglón es un renglón del acta, y un renglón que
// empieza con guion, viñeta o asterisco ("- Abrir cuentas", "-Firmar cheques",
// "• …") sale como viñeta. Antes la resolución juntaba todo en un párrafo.

const MARCA_VINETA = /^[-–—•*·]\s*(?=\S)/;

/** Renglones de un texto libre: [{ vineta, texto }] (los vacíos se descartan). */
export function lineasDe(texto) {
  return String(texto || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const m = MARCA_VINETA.exec(l);
      return m ? { vineta: true, texto: l.slice(m[0].length).trim() } : { vineta: false, texto: l };
    });
}

/** Un renglón partido si supera `max` (por oraciones): la viñeta va solo en el primer tramo. */
const tramosDeLinea = (linea, max) =>
  partirParrafo(linea.texto, max).map((texto, k) => ({ vineta: linea.vineta && k === 0, sigue: linea.vineta && k > 0, texto }));

/**
 * Texto de una caja (resolución, informativo) en tramos de hasta `max`
 * caracteres que respetan los renglones: cada tramo es una lista de
 * renglones [{ vineta, sigue, texto }] y ninguno se corta salvo que solo
 * pase de `max`.
 */
export function tramosConFormato(texto, max = MAX_CARACTERES_PARRAFO) {
  const tramos = [];
  let actual = [];
  let largo = 0;
  for (const linea of lineasDe(texto)) {
    for (const r of tramosDeLinea(linea, max)) {
      if (actual.length && largo + r.texto.length > max) {
        tramos.push(actual);
        actual = [];
        largo = 0;
      }
      actual.push(r);
      largo += r.texto.length;
    }
  }
  if (actual.length) tramos.push(actual);
  return tramos;
}

/**
 * Bloques del cuerpo de un punto: cada párrafo es un bloque (como antes) y las
 * viñetas seguidas van juntas en uno, de hasta `max` caracteres.
 */
export function bloquesDeTexto(texto, max = MAX_CARACTERES_PARRAFO) {
  const bloques = [];
  let lista = null;
  let largo = 0;
  for (const linea of lineasDe(texto)) {
    if (!linea.vineta) {
      lista = null;
      tramosDeLinea(linea, max).forEach((r) => bloques.push([r]));
      continue;
    }
    for (const r of tramosDeLinea(linea, max)) {
      if (!lista || largo + r.texto.length > max) {
        lista = [];
        largo = 0;
        bloques.push(lista);
      }
      lista.push(r);
      largo += r.texto.length;
    }
  }
  return bloques;
}
